import React from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { AsyncSelect } from '@dispatch/web/ui';
import { useGetResourcesPaginated } from '@dispatch/shared/hooks';
import { useDebounce } from 'react-use';
import type { Control, FieldValues } from 'react-hook-form';
import type { MultiSelectOption } from '@dispatch/shared/types';

export type CopySource = {
  id: string;
  name: string;
};

export type CopySourceSelectorProps = {
  control: Control<FieldValues>;
  readOnly?: boolean;
  onSelect: (source: CopySource | null) => void;
};

/**
 * Opt-in copy-from-resource control. A "Copy values from resource" checkbox is
 * shown up front; only when it is checked does the single-select, type-ahead
 * resource search appear. Unchecking clears any chosen source so the update
 * reverts to a normal manual bulk update. The search renders an informational
 * empty state when the elastic name search returns no match.
 */
export function CopySourceSelector({
  control,
  readOnly = false,
  onSelect,
}: CopySourceSelectorProps): JSX.Element {
  const [copyEnabled, setCopyEnabled] = React.useState<boolean>(false);
  const [input, setInput] = React.useState<string>('');
  const [debouncedInput, setDebouncedInput] = React.useState<string>('');

  useDebounce(
    () => {
      setDebouncedInput(input);
    },
    1000,
    [input]
  );

  const filteredResources = useGetResourcesPaginated({
    filters: {
      name: debouncedInput,
      pageSize: 25,
    },
    enabled: copyEnabled && !!debouncedInput,
  });

  const options = React.useMemo<MultiSelectOption[]>(() => {
    return (
      filteredResources?.data?.map((resource) => ({
        value: resource.integrationGUID,
        title: resource.name,
      })) ?? []
    );
  }, [filteredResources?.data]);

  const isSearching =
    !!debouncedInput && filteredResources?.status === 'loading';
  const hasError = filteredResources?.status === 'error';
  const noMatch =
    !!debouncedInput && !isSearching && !hasError && options.length === 0;

  const handleValueChange = React.useCallback(
    (value: MultiSelectOption | MultiSelectOption[] | null) => {
      if (!value || Array.isArray(value)) {
        onSelect(null);
        return;
      }

      onSelect({ id: String(value.value), name: value.title });
    },
    [onSelect]
  );

  const handleToggle = React.useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setCopyEnabled(checked);
      if (!checked) {
        setInput('');
        setDebouncedInput('');
        onSelect(null);
      }
    },
    [onSelect]
  );

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={copyEnabled}
            onChange={handleToggle}
            disabled={readOnly}
          />
        }
        label="Copy values from resource"
      />
      {copyEnabled && (
        <>
          <AsyncSelect
            id="copySourceResourceId"
            name="copySourceResourceId"
            label="Resource"
            control={control}
            readOnly={readOnly}
            multiple={false}
            options={options}
            loading={isSearching}
            setInput={setInput}
            onValueChange={handleValueChange}
          />
          {hasError && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              Unable to search resources. Please try again.
            </Typography>
          )}
          {noMatch && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              No resources match &ldquo;{debouncedInput}&rdquo;. Refine your
              search to pick a resource.
            </Typography>
          )}
        </>
      )}
    </>
  );
}
